import { useEffect } from 'react';

import { Status, useScript } from '@betterment/js-runtime';

import appConfig from '../Shared/appConfig';
import { urlBuilder } from '../Shared/urlBuilder';

/**
 * TypeScript definitions for Zendesk Web Widget (Messenger)
 * Documentation: https://developer.zendesk.com/api-reference/widget-messaging/introduction/
 */
interface ZendeskMessenger {
  (command: 'messenger', action: 'open'): void;
  (command: 'messenger', action: 'close'): void;
  (command: 'messenger', action: 'show'): void;
  (command: 'messenger', action: 'hide'): void;
  (command: 'messenger', action: 'loginUser', callback: (callback: (jwtToken: string) => void) => void): void;
  (command: 'messenger', action: 'logoutUser'): void;
  (command: 'messenger:on', event: 'open' | 'close', callback: () => void): void;
}

declare global {
  interface Window {
    zE?: ZendeskMessenger;
    zEACLoaded?: boolean;
  }
}

interface ZendeskAuthResponse {
  token: string;
}

async function getZendeskAuthToken(): Promise<string> {
  const response = await fetch(urlBuilder.getZendeskChatbotAuthentication());
  if (!response.ok) throw new Error('HTTP request to generate Zendesk auth token failed');
  const data = (await response.json()) as ZendeskAuthResponse;
  return data.token;
}

export function useZendeskChatbotWidget(enabled: boolean): void {
  const { status } = useScript(enabled ? appConfig.zendeskChatbotScriptSrc : '', {
    attributes: { defer: true, id: 'ze-snippet' },
  });

  useEffect(() => {
    if (status === Status.READY && window.zE && enabled) {
      window.zE('messenger', 'loginUser', async (callback: (token: string) => void) => {
        try {
          callback(await getZendeskAuthToken());
        } catch (cause) {
          if (window.zE) {
            window.zE('messenger', 'close');
            window.zE('messenger', 'hide');
          }
        }
      });
    }
  }, [status, enabled]);

  // If this hook unmounts, hide the widget
  useEffect(() => {
    return () => {
      if (window.zE) {
        window.zE('messenger', 'close');
        window.zE('messenger', 'hide');
      }
    };
  }, []);

  // Show/hide when enabled changes
  useEffect(() => {
    if (status === Status.READY && window.zE) {
      if (enabled) {
        window.zE('messenger', 'show');
      } else {
        window.zE('messenger', 'close');
        window.zE('messenger', 'hide');
        window.zE('messenger', 'logoutUser');
      }
    }
  }, [enabled, status]);
}
